import React, { useState, useMemo, useEffect, useRef } from "react";
import DashboardContent from "../../components/DashboardContent";
import { usePermissions } from "../../contexts/PermissionsContext";
import { NO_PERMISSION_TITLE, PERMISSIONS } from "../../utils/permissions";
import useCsr from "../../hooks/useCsr";
import RedirectHandler from "../../components/RedirectHandler";
import { useParams } from "react-router-dom";
import DataMappingContent from "../../components/DataMappingContent";
import { CsrAPI } from "../../apis/CsrAPI";
import { toast, ToastContainer } from "react-toastify";
import { CButton, CCollapse, CSpinner } from "@coreui/react";
import * as Sentry from "@sentry/react";
import { useScsTableOptions } from "../../hooks/useScsTableOptions";
import { useScsDescriptionOfStudies } from "../../hooks/useScsDescriptionOfStudies";
import { getApiErrorMessage } from "../../utils/apiError";

// Only use Sentry in production environment
const isProduction = process.env.NODE_ENV === "production";

// The page renders one accordion per grouping named in Q2A of the Description of
// Studies page. With no grouping named there, the questions are shown plainly
// (no accordion) and their selections are held under this internal key — it is
// never displayed, and never leaves the page (see handleSaveClick).
const UNGROUPED_KEY = "__ungrouped__";

// GET /mapping answers 202 while the background job is still crunching, so we
// re-ask on this cadence until it answers 200 – and give up after the deadline
// so a wedged job can't poll forever.
const MAPPING_POLL_INTERVAL_MS = 5000;
const MAPPING_POLL_TIMEOUT_MS = 5 * 60 * 1000;

// Factory returning a fresh, empty selections object for a single grouping.
const createEmptySelections = () => ({
  dispositionOfParticipants: [],
  demography: [],
  diseaseCharacteristics: [],
  death: [],
  discontinuation: [],
  frequency: [],
  otherSignificant: [],
  safetySummary: [],
  serious: [],
  severity: [],
  specialInterest: [],
  treatmentRelated: [],
  protocolDeviations: [],
  primaryEfficacy: [],
  secondaryEfficacy: [],
  dsHematology: [],
  avHematology: [],
  dsSerumChemistry: [],
  avSerumChemistry: [],
  dsUrinalysis: [],
  avUrinalysis: [],
  dsSpecialInterest: [],
  avSpecialInterest: [],
  dsVitalSigns: [],
  avVitalSigns: [],
  dsEcg: [],
  avEcg: [],
  medicalHistory: [],
  diseaseMedicalHistory: [],
  concomitantTherapy: [],
  diseaseConcomitantTherapy: [],
  exposure: [],
  compliance: [],
  diseaseCompliance: [],
});

// Does this object look like a saved selections mapping (rather than a wrapper
// keyed by grouping name)?
const looksLikeSelections = value =>
  Boolean(value) &&
  typeof value === "object" &&
  Object.keys(createEmptySelections()).some(key => key in value);

// Grouping names are matched on this form, so a saved mapping isn't lost to a
// difference in casing or spacing between the payload's keys and Q2A.
const normalizeName = name =>
  (name || "").trim().replace(/\s+/g, " ").toLowerCase();

// Map a GET /mapping payload (the TriloDocs proposal, or the user's saved edits
// to it) into the per-grouping selection shape the dropdowns render, one entry
// per key in `groupingKeys`. The payload's exact envelope isn't fixed, so we
// probe the likely locations and merge each grouping onto a fresh empty template
// (unknown keys are dropped, missing keys default to []). Returns null when
// nothing recognizable is found so callers keep their current state.
//
// A payload keyed per grouping — { "<grouping name>": {...} }, optionally under
// `dataMapping` / `groupings` — is paired up with the groupings on screen by
// name; a single flat mapping is applied to every grouping.
const buildGroupingSelections = (payload, groupingKeys) => {
  const dataMapping = payload?.dataMapping || payload;
  if (!dataMapping || typeof dataMapping !== "object") return null;

  const fill = pick =>
    groupingKeys.reduce((acc, grouping, index) => {
      const saved = pick(grouping, index);
      acc[grouping] = {
        ...createEmptySelections(),
        ...(saved && typeof saved === "object" ? saved : {}),
      };
      return acc;
    }, {});

  // Flat payload: a single mapping applied to every grouping. This is also the
  // shape saved while no grouping is named (see handleSaveClick).
  if (looksLikeSelections(dataMapping)) return fill(() => dataMapping);

  const groupings = dataMapping.groupings || dataMapping;
  const savedPerGrouping = Object.entries(groupings).filter(([, saved]) =>
    looksLikeSelections(saved)
  );
  if (savedPerGrouping.length === 0) return null;

  const savedByName = new Map(
    savedPerGrouping.map(([name, saved]) => [normalizeName(name), saved])
  );
  if (groupingKeys.some(grouping => savedByName.has(normalizeName(grouping)))) {
    return fill(grouping => savedByName.get(normalizeName(grouping)));
  }

  // Not one name matched: the mapping is keyed by names this page doesn't show —
  // the groupings were renamed in Q2A since, or the backend keyed them from
  // another answer. Pair them up by position rather than dropping the mapping;
  // that also covers the single ungrouped question set, which takes the first.
  return fill((grouping, index) => savedPerGrouping[index]?.[1]);
};

const SCSDataMapping = () => {
  const { wrongEnvironment, setAjaxLoading } = useCsr();
  const { studyId } = useParams();
  const { hasPermission } = usePermissions();

  // scs:wizards:read gates GET /mapping, which pre-fills the dropdowns.
  // Writing the mapping back — Save, and the Auto-Map job that proposes one —
  // needs scs:wizards:update on top of it.
  const canReadWizards = hasPermission(PERMISSIONS.scsWizardsRead);
  const canSaveWizards =
    canReadWizards && hasPermission(PERMISSIONS.scsWizardsUpdate);

  // Grouping names come from Q2A of the Description of Studies page, held in the
  // scsData store branch (fetched by the hook when landed on directly).
  const {
    groupingNames,
    isLoadingData: isLoadingGroupings,
    fetched: groupingsFetched,
  } = useScsDescriptionOfStudies(studyId);
  const hasGroupings = groupingNames.length > 0;
  // Until the answers are in we don't know how many question sets to draw, so
  // hold the content back rather than flashing the ungrouped layout first.
  const isResolvingGroupings = isLoadingGroupings && !groupingsFetched;

  // The keys the selections are held under: the grouping names, or the single
  // ungrouped key while none has been named.
  const groupingKeys = useMemo(
    () => (hasGroupings ? groupingNames : [UNGROUPED_KEY]),
    [hasGroupings, groupingNames]
  );

  // One selections object per grouping key.
  const [groupingSelections, setGroupingSelections] = useState(() => ({
    [UNGROUPED_KEY]: createEmptySelections(),
  }));

  // Accordion open/closed state – first grouping expanded by default.
  const [openGroupings, setOpenGroupings] = useState(() => new Set());

  const errRef = useRef();
  const [errMsg, setErrMsg] = useState("");
  const [showRedirect, setShowRedirect] = useState(false);
  const [isAutoMapping, setIsAutoMapping] = useState(false);

  // Auto-Map polls in a loop, so it needs to know when the page has gone away.
  const mountedRef = useRef(true);
  const pollTimerRef = useRef(null);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, []);

  // SCS table options come from the safety-summary TFL content that
  // CsrProvider loads into the scsData store branch once every uploaded file
  // has been PROCESSED. When it's available every dropdown (and the Auto-Map
  // with TriloDocs button) enables automatically.
  const { tableOptions, isDataReady, isLoadingData, dataError } =
    useScsTableOptions();

  // Shared data shared by all dropdowns across every grouping.
  const sharedTableData = useMemo(
    () => ({ tableOptions, isDataReady, isLoadingData, dataError }),
    [tableOptions, isDataReady, isLoadingData, dataError]
  );

  // The last mapping payload seen (saved edits, the TriloDocs proposal, or an
  // Auto-Map result). Kept so a grouping that appears after it arrived — the
  // names are fetched asynchronously — can still be pre-filled from it.
  const mappingPayloadRef = useRef(null);
  const groupingKeysRef = useRef(groupingKeys);
  groupingKeysRef.current = groupingKeys;

  // Apply a mapping payload to every grouping; returns whether anything
  // recognizable was found.
  const applyMapping = payload => {
    mappingPayloadRef.current = payload;
    const mapped = buildGroupingSelections(payload, groupingKeysRef.current);
    if (mapped) setGroupingSelections(mapped);
    return Boolean(mapped);
  };

  // Re-key the selections whenever the grouping names change: the names load
  // after the first render, and can be edited on the Description of Studies page
  // between visits. Selections already made for a grouping that is still around
  // are kept; a new grouping starts from the saved mapping when there is one.
  useEffect(() => {
    // Built for the whole key set at once, so the payload is paired up with the
    // groupings the same way it would have been had it arrived last.
    const fromMapping = buildGroupingSelections(
      mappingPayloadRef.current,
      groupingKeys
    );
    setGroupingSelections(prev =>
      groupingKeys.reduce((acc, grouping) => {
        acc[grouping] =
          prev[grouping] || fromMapping?.[grouping] || createEmptySelections();
        return acc;
      }, {})
    );
    // Only the first grouping is expanded to begin with.
    setOpenGroupings(new Set(groupingKeys.slice(0, 1)));
  }, [groupingKeys]);

  // Restore the dropdown selections when a user revisits an existing journey.
  // GET /mapping serves the user's saved edits when they exist and the
  // TriloDocs proposal otherwise, so it is the single source for pre-filling.
  // Anything other than 200 just leaves the empty defaults in place: 202 means
  // a job is still running, 404 that nothing has been mapped yet, and 409 that
  // a job stalled — in every case the user can (re-)run Auto-Map.
  useEffect(() => {
    if (!studyId) return undefined;
    // Without scs:wizards:read the endpoint is never called; the dropdowns
    // start from their empty defaults instead.
    if (!canReadWizards) return undefined;
    let cancelled = false;

    CsrAPI.getSafetySummaryMapping({ summaryId: studyId })
      .then(({ status, data }) => {
        if (cancelled || status !== 200) return;
        applyMapping(data);
      })
      .catch(error => {
        if (cancelled) return;
        const status = error?.response?.status;
        if (status === 403) {
          setShowRedirect(true);
        } else if (status !== 404 && status !== 409) {
          if (isProduction) Sentry.captureException(error);
          toast.error(
            getApiErrorMessage(error, "Failed to load saved data mapping.")
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [studyId, canReadWizards]);

  // 403/404/409 are documented for both halves of the Auto-Map flow, and the
  // backend supplies the precise wording each time; only the fallback differs
  // by stage (queueing the job vs. collecting its result).
  const reportMappingError = (error, conflictFallback, genericFallback) => {
    const status = error?.response?.status;
    if (status === 403) {
      setShowRedirect(true);
    } else if (status === 404) {
      toast.error(getApiErrorMessage(error, "Summary not found."));
    } else if (status === 409) {
      toast.warning(getApiErrorMessage(error, conflictFallback));
    } else {
      if (isProduction) Sentry.captureException(error);
      toast.error(getApiErrorMessage(error, genericFallback));
    }
  };

  const wait = ms =>
    new Promise(resolve => {
      pollTimerRef.current = setTimeout(resolve, ms);
    });

  // Poll GET /mapping until the queued job produces the proposed mapping: 200
  // resolves with the payload, 202 means keep waiting, and anything else throws
  // for the caller's error handling. Resolves null when we give up on the
  // deadline or the page unmounted mid-poll.
  const pollForMapping = async () => {
    const deadline = Date.now() + MAPPING_POLL_TIMEOUT_MS;

    for (;;) {
      const { status, data } = await CsrAPI.getSafetySummaryMapping({
        summaryId: studyId,
      });
      if (!mountedRef.current) return null;
      if (status === 200) return data;

      if (Date.now() >= deadline) {
        toast.warning(
          "Auto-mapping is taking longer than expected. Please try again shortly."
        );
        return null;
      }

      await wait(MAPPING_POLL_INTERVAL_MS);
      if (!mountedRef.current) return null;
    }
  };

  // "Auto-Map with TriloDocs": POST /mapping queues the job (202 = queued),
  // then GET /mapping is polled until it returns the proposed mapping, which
  // pre-fills every grouping's dropdowns.
  const handleAutoMapClick = async () => {
    if (!studyId) {
      toast.error("Summary ID not found");
      return;
    }
    if (!canSaveWizards) return;

    let queued = false;
    setIsAutoMapping(true);
    try {
      const { status } = await CsrAPI.submitSafetySummaryMapping({
        summaryId: studyId,
      });
      if (status !== 202) {
        toast.info("Auto-mapping could not be started.");
        return;
      }
      queued = true;

      const data = await pollForMapping();
      if (!mountedRef.current || !data) return;

      if (applyMapping(data)) {
        toast.success("Table mapping updated successfully!");
      } else {
        toast.info("No mapping data received.");
      }
    } catch (error) {
      if (!mountedRef.current) return;
      if (queued) {
        // The job stalled and needs re-running (409), or the fetch failed.
        reportMappingError(
          error,
          "Data mapping stalled — please run Auto-Map again.",
          "Failed to fetch the mapping results."
        );
      } else {
        // Can't start: no files uploaded, files still processing, or a mapping
        // job is already running (all 409).
        reportMappingError(
          error,
          "Data mapping cannot start yet — please try again shortly.",
          "Failed to start auto-mapping."
        );
      }
    } finally {
      if (mountedRef.current) setIsAutoMapping(false);
    }
  };

  // Toggle a grouping's accordion open/closed
  const toggleGrouping = grouping => {
    setOpenGroupings(prev => {
      const next = new Set(prev);
      next.has(grouping) ? next.delete(grouping) : next.add(grouping);
      return next;
    });
  };

  // Build a setSelections handler scoped to a single grouping. Supports both
  // functional updates and direct value updates (matching DataMappingContent).
  const makeSetSelections = grouping => updater => {
    setGroupingSelections(prev => ({
      ...prev,
      [grouping]:
        typeof updater === "function" ? updater(prev[grouping]) : updater,
    }));
  };

  // Persist the dropdown selections with PATCH /wizards/data_mapping. Runs both
  // from the Save button and from the "SCS File" button (DashboardContent saves
  // before navigating), so it resolves/rejects for the navigation handler.
  const handleSaveClick = async () => {
    if (!canSaveWizards) return Promise.resolve();

    setAjaxLoading(true);
    // The selections are the body as-is, with no wrapping key: keyed by grouping
    // name when the user named some in Q2A, and a single flat mapping otherwise,
    // so the internal ungrouped key is never persisted (and still restores
    // through buildGroupingSelections' flat-payload branch).
    const state = hasGroupings
      ? groupingSelections
      : groupingSelections[UNGROUPED_KEY] || createEmptySelections();

    return CsrAPI.saveSafetySummaryDataMapping({ summaryId: studyId, state })
      .then(() => {
        setErrMsg("");
        toast.success("Data mapping saved successfully!");
        return Promise.resolve();
      })
      .catch(error => {
        if (isProduction) {
          Sentry.captureException(error);
        }
        const status = error?.response?.status;
        if (!error?.response) {
          setErrMsg("No Server Response");
        } else if (status === 403) {
          setShowRedirect(true);
        } else if (status === 404) {
          toast.error(getApiErrorMessage(error, "Summary not found."));
        } else {
          toast.error(
            getApiErrorMessage(error, "Failed to save data mapping.")
          );
        }
        errRef.current?.focus();
        return Promise.reject(error);
      })
      .finally(() => {
        setAjaxLoading(false);
      });
  };

  const PageContent = () => {
    return (
      // `scs-dos` carries no styling of its own; it scopes the shared SCS
      // wizard rules (the accordion controls below) the same way the Subject
      // Disposition page picks them up.
      <div className="scs-dos">
        <span
          ref={errRef}
          className={errMsg ? "errmsg" : "offscreen"}
          aria-live="assertive">
          {errMsg}
        </span>

        {/* Single shared Auto-Map button at the top of the page (mirrors the
            CSR Data Mapping page). Applies to every grouping below. */}
        <div className="questions-content__item">
          <div className="questions-content__label intro-text">
            <span className="questions-content__label-text">
              Select tables to map, or
            </span>

            <CButton
              className="input-submit input-submit__primary input-submit__arrow"
              disabled={
                isAutoMapping ||
                !canSaveWizards ||
                !sharedTableData.tableOptions ||
                sharedTableData.tableOptions.length === 0
              }
              title={canSaveWizards ? undefined : NO_PERMISSION_TITLE}
              onClick={e => {
                e.preventDefault();
                handleAutoMapClick();
              }}>
              {isAutoMapping ? (
                <>
                  <CSpinner size="sm" className="me-2" />
                  Auto-Mapping...
                </>
              ) : (
                "Auto-Map with TriloDocs"
              )}
            </CButton>
          </div>
        </div>

        {/* One accordion per grouping named in Q2A of the Description of
            Studies page; with none named the questions are shown plainly. */}
        {isResolvingGroupings ? (
          <div className="questions-content__item">
            <CSpinner size="sm" className="me-2" />
          </div>
        ) : hasGroupings ? (
          <>
            {/* Expand all / Collapse all, as on the Description of Studies and
                Subject Disposition pages — only worth showing for more than
                one grouping. */}
            {groupingNames.length > 1 && (
              <div className="scs-dos__accordion-controls">
                <CButton
                  color="primary"
                  shape="pill"
                  className="scs-dos__duplicate"
                  onClick={() => setOpenGroupings(new Set(groupingNames))}>
                  Expand all
                </CButton>
                <CButton
                  color="primary"
                  shape="pill"
                  className="scs-dos__duplicate"
                  onClick={() => setOpenGroupings(new Set())}>
                  Collapse all
                </CButton>
              </div>
            )}
            <div className="accordion">
              {groupingNames.map(grouping => {
                const isOpen = openGroupings.has(grouping);
                return (
                  <div key={grouping} className="accordion-item">
                    <div className="accordion-header">
                      <button
                        type="button"
                        className={`accordion-button${isOpen ? "" : " collapsed"}`}
                        onClick={() => toggleGrouping(grouping)}>
                        {grouping}
                      </button>
                    </div>
                    <CCollapse visible={isOpen} className="accordion-collapse">
                      <div className="accordion-body">
                        <DataMappingContent
                          selections={groupingSelections[grouping]}
                          setSelections={makeSetSelections(grouping)}
                          sharedTableData={sharedTableData}
                          showMapButton={false}
                          showDispositionOfParticipants={true}
                          disableDropdowns={isAutoMapping}
                        />
                      </div>
                    </CCollapse>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <DataMappingContent
            selections={groupingSelections[UNGROUPED_KEY]}
            setSelections={makeSetSelections(UNGROUPED_KEY)}
            sharedTableData={sharedTableData}
            showMapButton={false}
            showDispositionOfParticipants={true}
            disableDropdowns={isAutoMapping}
          />
        )}

        <ToastContainer
          position="top-right"
          autoClose={2000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick={true}
          rtl={false}
          pauseOnFocusLoss={false}
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    );
  };

  if (showRedirect || wrongEnvironment) {
    return <RedirectHandler />;
  }

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle={"Data Mapping"}
      headerButton={false}
      pageContent={PageContent()}
      prevButton={{
        buttonTitle: "Post-Marketing Data",
        buttonGoToAction: `/scs/${studyId}/post-marketing-data`,
      }}
      nextButton={{
        buttonTitle: "SCS File",
        buttonGoToAction: `/scs/${studyId}/scs-file`,
      }}
      saveButton={true}
      savePermission={[
        PERMISSIONS.scsWizardsRead,
        PERMISSIONS.scsWizardsUpdate,
      ]}
      saveButtonHandler={handleSaveClick}
    />
  );
};

export default SCSDataMapping;
